import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Upload, ImagePlus, GripVertical, ArrowUp } from "lucide-react";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "gallery", label: "Gallery" },
  { value: "room", label: "Accommodation" },
  { value: "activity", label: "Activities" },
  { value: "dining", label: "Dining · Sunshine Kitchen" },
  { value: "bar", label: "Dining · Bablu Da Theka (Bar)" },
  { value: "heritage_hall", label: "Dining · Heritage Hall" },
  { value: "orchard", label: "Orchard" },
  { value: "heritage", label: "Heritage" },
  { value: "banquet", label: "Banquets & Events" },
  { value: "past_events", label: "Past Events" },
];

/**
 * A single draggable photo card inside the sortable grid.
 */
function PhotoCard({ photo, onDelete, isFirst }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 40 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`photo-${photo.id}`}
      className="group relative border border-white/10 bg-[#27221C]"
    >
      <img src={photo.url} alt={photo.title} className="w-full aspect-square object-cover pointer-events-none select-none" />

      {isFirst && (
        <div className="absolute top-2 left-2 bg-[#D97645] text-[#1C1913] text-[10px] font-mono uppercase tracking-widest px-2 py-1">
          Primary
        </div>
      )}

      {/* Drag handle */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`drag-handle-${photo.id}`}
        className="absolute top-2 right-2 p-1.5 bg-[#1C1913]/80 hover:bg-[#1C1913] cursor-grab active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical size={14} className="text-[#F2E7D0]" />
      </button>

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-[#1C1913]/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-4 pointer-events-none">
        <div>
          <div className="chapter-num">{photo.category}</div>
          {photo.title && <div className="mt-1 font-display text-xl text-[#F2E7D0]">{photo.title}</div>}
        </div>
        <div className="flex justify-end pointer-events-auto">
          <Button
            data-testid={`delete-photo-${photo.id}`}
            onClick={() => onDelete(photo.id)}
            variant="ghost"
            size="icon"
            className="text-[#F2E7D0] hover:bg-[#D97645]/30"
          >
            <Trash2 size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}


export default function AdminPhotos({ token }) {
  const [allPhotos, setAllPhotos] = useState([]);
  const [uploadCategory, setUploadCategory] = useState("gallery");
  const [filter, setFilter] = useState("gallery");
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/photos`);
      setAllPhotos(data);
    } catch (e) {
      toast.error("Could not load photos");
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const folder = `tikratoli/${uploadCategory}`;
      const { data: sig } = await axios.get(`${API}/photos/signature`, { ...auth, params: { folder } });
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: "POST", body: form });
      const cld = await res.json();
      if (!cld.secure_url) throw new Error(cld.error?.message || "Upload failed");
      await axios.post(`${API}/photos`, {
        url: cld.secure_url,
        public_id: cld.public_id,
        category: uploadCategory,
        title,
      }, auth);
      toast.success("Photo uploaded — it is now the primary in its category");
      setTitle("");
      setFilter(uploadCategory);
      if (fileInput.current) fileInput.current.value = "";
      load();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/photos/${id}`, auth);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const visible = allPhotos.filter((p) => p.category === filter);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = visible.findIndex((p) => p.id === active.id);
    const newIndex = visible.findIndex((p) => p.id === over.id);
    const newOrder = arrayMove(visible, oldIndex, newIndex);

    // Optimistic update
    setAllPhotos((prev) => {
      const others = prev.filter((p) => p.category !== filter);
      return [...others, ...newOrder];
    });

    try {
      await axios.post(
        `${API}/photos/reorder`,
        { category: filter, ordered_ids: newOrder.map((p) => p.id) },
        auth,
      );
      // Silent success — no toast noise on every drag
    } catch (e) {
      toast.error("Could not save new order");
      load(); // revert
    }
  };

  const promoteToTop = async (id) => {
    const target = visible.find((p) => p.id === id);
    if (!target) return;
    const rest = visible.filter((p) => p.id !== id);
    const newOrder = [target, ...rest];
    setAllPhotos((prev) => {
      const others = prev.filter((p) => p.category !== filter);
      return [...others, ...newOrder];
    });
    try {
      await axios.post(
        `${API}/photos/reorder`,
        { category: filter, ordered_ids: newOrder.map((p) => p.id) },
        auth,
      );
      toast.success("Now the primary photo for this category");
    } catch (e) {
      toast.error("Could not update");
      load();
    }
  };

  return (
    <div data-testid="admin-photos" className="space-y-8">
      {/* Upload panel */}
      <div className="border border-white/10 p-6">
        <div className="flex items-center gap-3 mb-6">
          <ImagePlus size={18} className="text-[#D97645]" />
          <div className="chapter-num">Upload New Photo</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="font-mono-label text-[#B8A891]">Category</Label>
            <Select value={uploadCategory} onValueChange={setUploadCategory}>
              <SelectTrigger data-testid="upload-category" className="mt-2 bg-transparent border-white/20 rounded-none h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#27221C] border-white/20 rounded-none">
                {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-mono-label text-[#B8A891]">Title (optional)</Label>
            <Input
              data-testid="upload-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-2 bg-transparent border-white/20 rounded-none h-11"
              placeholder="e.g. Sunrise over orchard"
            />
          </div>
          <div className="flex items-end">
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              data-testid="upload-file-input"
              onChange={(e) => upload(e.target.files?.[0])}
              className="hidden"
            />
            <Button
              data-testid="upload-btn"
              onClick={() => fileInput.current?.click()}
              disabled={uploading}
              className="rounded-none bg-[#D97645] hover:bg-[#C0602F] text-[#F2E7D0] font-mono-label h-11 w-full"
            >
              <Upload size={14} className="mr-2" /> {uploading ? "Uploading…" : "Choose & Upload"}
            </Button>
          </div>
        </div>
        <p className="mt-4 text-xs text-[#B8A891]">
          New uploads become the <span className="text-[#D97645]">primary</span> photo for their category. Drag to reorder — the first photo shows in the hero slot on the public page.
        </p>
      </div>

      {/* Filter + Grid */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="chapter-num">Library — {filter} ({visible.length})</div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="filter-category" className="bg-transparent border-white/20 rounded-none w-48 h-9 font-mono-label text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#27221C] border-white/20 rounded-none">
            {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-[#B8A891]">
          Drag cards to reorder · First card = primary hero photo
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="text-center py-16 text-[#B8A891] border border-white/10 border-dashed">
          No photos in this category yet. Upload one above.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visible.map((p) => p.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {visible.map((p, i) => (
                <div key={p.id} className="relative group">
                  <PhotoCard photo={p} onDelete={del} isFirst={i === 0} />
                  {i !== 0 && (
                    <button
                      type="button"
                      data-testid={`promote-${p.id}`}
                      onClick={() => promoteToTop(p.id)}
                      className="absolute bottom-2 left-2 opacity-0 group-hover:opacity-100 inline-flex items-center gap-1 px-2 py-1 bg-[#1C1913]/80 hover:bg-[#D97645] text-[#F2E7D0] text-[10px] font-mono uppercase tracking-widest transition"
                    >
                      <ArrowUp size={10} /> Make primary
                    </button>
                  )}
                </div>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
